import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { getApiErrorMessage } from '~/utils/getApiErrorMessage'

export type TierSlug = 'copper' | 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond'

export interface WealthTier {
  id: string
  slug: TierSlug
  name: string
  minXaut: number
  maxXaut: number | null
  icons: { small: string; medium: string; large: string }
  glowColor: string | null
  particleEffect: string | null
}

export interface TierProgress {
  currentTier: string
  nextTier: string | null
  progressPercent: number
  xautToNext: number | null
}

export interface TierBenefits {
  rakebackBonus: number
  chatFrame: boolean
  leaderboardDisplay: boolean
  specialEmotes: string[]
}

interface UserTierResponse {
  tier: WealthTier
  progress: TierProgress
  benefits: TierBenefits
  balance: string
  balanceUsd: string
}

export const useWealthTierStore = defineStore('wealthTier', () => {
  const config = useRuntimeConfig()

  // State
  const allTiers = ref<WealthTier[]>([])
  const currentTier = ref<WealthTier | null>(null)
  const progress = ref<TierProgress | null>(null)
  const benefits = ref<TierBenefits | null>(null)
  const balance = ref<string | null>(null)
  const balanceUsd = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const hasTier = computed(() => currentTier.value !== null)

  const tierSlug = computed(() => currentTier.value?.slug ?? null)

  const tierName = computed(() => currentTier.value?.name ?? 'Unknown')

  const progressPercent = computed(() => progress.value?.progressPercent ?? 0)

  const hasNextTier = computed(() => progress.value?.nextTier !== null)

  const rakebackBonus = computed(() => benefits.value?.rakebackBonus ?? 0)

  const hasChatFrame = computed(() => benefits.value?.chatFrame ?? false)

  const hasLeaderboardDisplay = computed(() => benefits.value?.leaderboardDisplay ?? false)

  const specialEmotes = computed(() => benefits.value?.specialEmotes ?? [])

  const currentTierIcon = computed(() => currentTier.value?.icons ?? null)

  const glowColor = computed(() => currentTier.value?.glowColor ?? null)

  // Actions
  async function fetchAllTiers(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await $fetch<WealthTier[]>(`${config.public.apiBase}/wealth-tiers`)
      allTiers.value = response
    } catch (err: unknown) {
      console.error('[wealthTierStore] Failed to fetch all tiers:', err)
      error.value = getApiErrorMessage(err, 'Failed to fetch tier definitions')
    } finally {
      isLoading.value = false
    }
  }

  async function fetchUserTier(accessToken: string): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await $fetch<UserTierResponse>(`${config.public.apiBase}/wealth-tier`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      currentTier.value = response.tier
      progress.value = response.progress
      benefits.value = response.benefits
      balance.value = response.balance
      balanceUsd.value = response.balanceUsd
    } catch (err: unknown) {
      console.error('[wealthTierStore] Failed to fetch user tier:', err)
      error.value = getApiErrorMessage(err, 'Failed to fetch user tier')
    } finally {
      isLoading.value = false
    }
  }

  function getTierBySlug(slug: TierSlug): WealthTier | undefined {
    return allTiers.value.find(tier => tier.slug === slug)
  }

  function clearUserTier(): void {
    currentTier.value = null
    progress.value = null
    benefits.value = null
    balance.value = null
    balanceUsd.value = null
    error.value = null
  }

  return {
    // State
    allTiers,
    currentTier,
    progress,
    benefits,
    balance,
    balanceUsd,
    isLoading,
    error,
    // Getters
    hasTier,
    tierSlug,
    tierName,
    progressPercent,
    hasNextTier,
    rakebackBonus,
    hasChatFrame,
    hasLeaderboardDisplay,
    specialEmotes,
    currentTierIcon,
    glowColor,
    // Actions
    fetchAllTiers,
    fetchUserTier,
    getTierBySlug,
    clearUserTier,
  }
})
